import React, { useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";

interface ExpandableAddSectionProps {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
}

export function ExpandableAddSection({
  label,
  icon,
  children,
  expanded,
  onToggle,
}: ExpandableAddSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  // Scroll into view when expanded
  useEffect(() => {
    if (expanded && sectionRef.current) {
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [expanded]);

  if (!expanded) {
    return (
      <div className="block">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground px-1 justify-start w-full"
          onClick={onToggle}
        >
          {icon || <Plus className="h-3 w-3 mr-1.5" />}
          {!icon && label}
          {icon && <span className="ml-1">{label}</span>}
        </Button>
      </div>
    );
  }

  return (
    <div ref={sectionRef} className="space-y-2 rounded-md border bg-muted/20 p-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground font-medium">
          {label}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0"
          onClick={onToggle}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
      {children}
    </div>
  );
}
